import React, {
  Component,
  type KeyboardEvent,
  type PointerEvent,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import classNames from 'classnames';
import { clamp } from 'lodash-es';
import _throttle from 'lodash-es/throttle';

import styles from './resizable.module.css';

const RESIZER_CONTROL_WIDTH = 5;
const KEYBOARD_STEP_PX = 16;
const PRIMARY_BUTTON = 0;

type Props = PropsWithChildren<{
  'data-scroll-into-id'?: string;
  className?: string;
  handleClassName?: string;
  /** Accessible name of the resize handle. */
  handleLabel?: string;
  defaultSize?: number;
  minWidth: number;
  maxWidth: number;
  side: 'left' | 'right';
  /** Called with the new content width once a drag or key press ends. */
  onResize?: (width: number) => void;
}>;

class Resizable extends Component<Props> {
  static defaultProps = {
    side: 'right',
    handleLabel: 'Resize panel',
  };

  ref = React.createRef<HTMLDivElement>();

  handleRef = React.createRef<HTMLDivElement>();

  state = {
    resizing: false,
  };

  prevWidths: number | undefined;

  prevLeft: number | undefined;

  width =
    (this.props.defaultSize ?? this.props.minWidth) + RESIZER_CONTROL_WIDTH;

  // Rounded, as a saved width can be fractional.
  getContentWidth = (): number =>
    Math.round(
      clamp(
        this.width - RESIZER_CONTROL_WIDTH,
        this.props.minWidth,
        this.props.maxWidth,
      ),
    );

  // Writes the width straight to the element, so resizing doesn't re-render.
  applyContentWidth = (contentWidth: number): void => {
    const rootElement = this.ref.current;
    this.prevWidths = this.width;
    this.width = contentWidth + RESIZER_CONTROL_WIDTH;
    if (rootElement) {
      rootElement.style.width = `${this.width}px`;
    }
    this.handleRef.current?.setAttribute('aria-valuenow', String(contentWidth));
  };

  handlePointerMove = _throttle((event: globalThis.PointerEvent) => {
    const { side, minWidth, maxWidth } = this.props;
    const rootElement = this.ref.current;
    if (!rootElement) {
      return;
    }
    const boundingClientRect = rootElement.getBoundingClientRect();
    const pointerDistance =
      side === 'right'
        ? event.clientX - boundingClientRect.left
        : boundingClientRect.right - event.clientX;
    if (side === 'left') {
      this.prevLeft = boundingClientRect.left;
    }
    this.applyContentWidth(
      clamp(Math.round(pointerDistance), minWidth, maxWidth),
    );
    this.ensureWidth();
  }, 16);

  ensureWidth = (): void => {
    const rootElement = this.ref.current;
    if (!rootElement) {
      return;
    }
    const { side } = this.props;
    if (side !== 'left') {
      return;
    }
    const boundingClientRect = rootElement.getBoundingClientRect();
    if (boundingClientRect.left === this.prevLeft && this.prevWidths) {
      this.width = this.prevWidths;
      rootElement.style.width = `${this.width}px`;
    }
  };

  handlePointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.button !== PRIMARY_BUTTON) {
      return;
    }
    event.preventDefault();
    this.setState({ resizing: true });
    document.addEventListener('pointermove', this.handlePointerMove);
    document.addEventListener('pointerup', this.handlePointerUp);
    document.addEventListener('pointercancel', this.handlePointerUp);
  };

  handlePointerUp = (): void => {
    this.handlePointerMove.flush();
    this.removePointerListeners();
    this.setState({ resizing: false });
    this.props.onResize?.(this.getContentWidth());
  };

  removePointerListeners = (): void => {
    document.removeEventListener('pointermove', this.handlePointerMove);
    document.removeEventListener('pointerup', this.handlePointerUp);
    document.removeEventListener('pointercancel', this.handlePointerUp);
  };

  handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const { side, minWidth, maxWidth } = this.props;
    const growKey = side === 'right' ? 'ArrowRight' : 'ArrowLeft';
    const shrinkKey = side === 'right' ? 'ArrowLeft' : 'ArrowRight';
    const contentWidth = this.getContentWidth();
    let nextWidth: number | undefined;
    if (event.key === growKey) {
      nextWidth = contentWidth + KEYBOARD_STEP_PX;
    } else if (event.key === shrinkKey) {
      nextWidth = contentWidth - KEYBOARD_STEP_PX;
    } else if (event.key === 'Home') {
      nextWidth = minWidth;
    } else if (event.key === 'End') {
      nextWidth = maxWidth;
    }
    if (nextWidth === undefined) {
      return;
    }
    event.preventDefault();
    const clampedWidth = clamp(nextWidth, minWidth, maxWidth);
    this.applyContentWidth(clampedWidth);
    this.props.onResize?.(clampedWidth);
  };

  componentWillUnmount(): void {
    this.handlePointerMove.cancel();
    this.removePointerListeners();
  }

  render(): ReactNode {
    const { minWidth, maxWidth, children, side, handleClassName, handleLabel } =
      this.props;
    return (
      <div
        data-scroll-into-id={this.props['data-scroll-into-id']}
        className={classNames(styles.root, this.props.className)}
        ref={this.ref}
        style={{
          minWidth: minWidth + RESIZER_CONTROL_WIDTH,
          maxWidth: maxWidth + RESIZER_CONTROL_WIDTH,
          width: this.width,
        }}
      >
        {side === 'right' ? children : null}
        <div
          ref={this.handleRef}
          role="separator"
          aria-orientation="vertical"
          aria-label={handleLabel}
          aria-valuenow={this.getContentWidth()}
          aria-valuemin={minWidth}
          aria-valuemax={maxWidth}
          tabIndex={0}
          className={classNames(styles.resizer_control, handleClassName, {
            [styles.active]: this.state.resizing,
          })}
          onPointerDown={this.handlePointerDown}
          onKeyDown={this.handleKeyDown}
        />
        {side === 'left' ? children : null}
      </div>
    );
  }
}

export default Resizable;
